import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DURATION_SECONDS = 75;

const scenes = [
  {
    at: 0,
    eyebrow: "Today",
    title: "One warranty claim. Information in six places.",
    body: "The technician has the diagnosis. The office needs the serial number, photos, parts, labor, and the manufacturer’s current rules.",
    visual: "scatter",
  },
  {
    at: 13,
    eyebrow: "At the job site",
    title: "The technician sees only what this claim requires.",
    body: "A guided checklist adjusts by manufacturer and equipment type—before the technician leaves.",
    visual: "capture",
  },
  {
    at: 29,
    eyebrow: "Before handoff",
    title: "Missing information is caught while it can still be fixed.",
    body: "Model, serial, eligibility, photos, diagnosis, parts, and labor are checked against the requirement set.",
    visual: "validate",
  },
  {
    at: 46,
    eyebrow: "In the office",
    title: "Evidence arrives organized around the claim.",
    body: "The office receives a complete claim packet and a draft—without searching texts, email, or the technician’s memory.",
    visual: "packet",
  },
  {
    at: 62,
    eyebrow: "Human-controlled",
    title: "Your team reviews, edits, and submits.",
    body: "The copilot prepares and flags. Your team owns accuracy, manufacturer communication, and the final submission.",
    visual: "review",
  },
];

type Props = {
  onEvent: (name: "demo_started" | "demo_paused" | "demo_progress" | "demo_completed", metadata?: Record<string, string | number | boolean>) => void;
  onReadyToContinue: () => void;
};

function SceneVisual({ type }: { type: string }) {
  if (type === "scatter") {
    return (
      <div className="demo-scatter" aria-hidden="true">
        {[
          "Portal rules",
          "Serial photo",
          "Tech notes",
          "Parts invoice",
          "Email thread",
          "Labor time",
        ].map((label, index) => <span key={label} style={{ "--i": index } as React.CSSProperties}>{label}</span>)}
      </div>
    );
  }
  if (type === "capture") {
    return (
      <div className="demo-phone" aria-hidden="true">
        <div className="phone-top">Claim capture · Step 4 of 6</div>
        <strong>Serial number photo</strong>
        <div className="camera-box"><span>SN 48K-22019</span></div>
        <div className="status-good">✓ Readable and matched</div>
      </div>
    );
  }
  if (type === "validate") {
    return (
      <div className="demo-checklist" aria-hidden="true">
        {["Eligibility confirmed", "Model + serial", "Diagnosis", "Required photos", "Parts + labor"].map((label, index) => (
          <div key={label}><span className={index === 3 ? "checking" : "checked"}>{index === 3 ? "…" : "✓"}</span>{label}</div>
        ))}
      </div>
    );
  }
  if (type === "packet") {
    return (
      <div className="demo-packet" aria-hidden="true">
        <div className="packet-page back" />
        <div className="packet-page middle" />
        <div className="packet-page front">
          <small>CLAIM PACKET</small>
          <strong>Ready for review</strong>
          <span>8 evidence items organized</span>
          <span>0 required fields missing</span>
        </div>
      </div>
    );
  }
  return (
    <div className="demo-review" aria-hidden="true">
      <div><small>Draft claim</small><strong>Human review required</strong></div>
      <button type="button" tabIndex={-1}>Review &amp; approve</button>
    </div>
  );
}

export function ConceptDemo({ onEvent, onReadyToContinue }: Props) {
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState(false);
  const sentMilestones = useRef(new Set<number>());
  const started = useRef(false);
  const completed = useRef(false);

  const scene = useMemo(() => {
    return [...scenes].reverse().find((candidate) => seconds >= candidate.at) ?? scenes[0];
  }, [seconds]);

  const toggle = useCallback(() => {
    setPlaying((current) => {
      const next = !current;
      if (next && !started.current) {
        started.current = true;
        onEvent("demo_started", { duration_seconds: DURATION_SECONDS });
      } else if (!next) {
        onEvent("demo_paused", { progress_seconds: seconds });
      }
      return next;
    });
  }, [onEvent, seconds]);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setSeconds((current) => Math.min(DURATION_SECONDS, current + 0.25));
    }, 250);
    return () => window.clearInterval(timer);
  }, [playing]);

  useEffect(() => {
    const percentage = Math.floor((seconds / DURATION_SECONDS) * 100);
    for (const milestone of [25, 50, 75, 100]) {
      if (percentage >= milestone && !sentMilestones.current.has(milestone)) {
        sentMilestones.current.add(milestone);
        onEvent("demo_progress", { percent: milestone });
      }
    }
    if (seconds >= DURATION_SECONDS && !completed.current) {
      completed.current = true;
      setPlaying(false);
      onEvent("demo_completed", { percent: 100 });
      onReadyToContinue();
    } else if (seconds >= 45) {
      onReadyToContinue();
    }
  }, [seconds, onEvent, onReadyToContinue]);

  const percentage = Math.min(100, Math.round((seconds / DURATION_SECONDS) * 100));

  return (
    <div className="concept-demo">
      <div className="demo-stage" aria-live="polite">
        <div className="demo-copy" key={scene.title}>
          <span>{scene.eyebrow}</span>
          <h3>{scene.title}</h3>
          <p>{scene.body}</p>
        </div>
        <div className="demo-visual"><SceneVisual type={scene.visual} /></div>
      </div>
      <div className="demo-controls">
        <button className="play-button" type="button" onClick={toggle} aria-label={playing ? "Pause concept demo" : "Play concept demo"}>
          {playing ? "Ⅱ" : "▶"}
        </button>
        <div className="demo-timeline" aria-label={`${percentage}% watched`}>
          <span style={{ width: `${percentage}%` }} />
        </div>
        <time>{Math.floor(seconds / 60)}:{String(Math.floor(seconds % 60)).padStart(2, "0")} / 1:15</time>
      </div>
      <details className="transcript">
        <summary>Read transcript</summary>
        <ol>{scenes.map((item) => <li key={item.title}><strong>{item.title}</strong> {item.body}</li>)}</ol>
      </details>
    </div>
  );
}
